import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { formatMoney } from '../lib/billing-api';

interface Report {
  estimatedCostUsd: number;
  unknownUsageCalls: number;
  unpricedCalls: number;
  groups: Array<{
    kind: string; provider: string; model: string; calls: number; failed: number; cached: number;
    inputTokens: number; outputTokens: number; billableReplies: number; billableOperations: number;
    operationAmount: number; currency: string; firstTokenP95Ms: number | null;
  }>;
}

export function ConversationAiUsage({ siteId, sessionId }: { siteId: string | undefined; sessionId: string }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!open || !siteId) return;
    let disposed = false;
    setReport(null);
    const refresh = () => void apiFetch<Report>(`/api/admin/sites/${siteId}/ai-usage/${sessionId}`)
      .then((value) => { if (!disposed) { setReport(value); setError(false); } })
      .catch(() => { if (!disposed) setError(true); });
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => { disposed = true; clearInterval(timer); };
  }, [siteId, sessionId, open]);
  if (!siteId) return null;
  return <details onToggle={(e) => setOpen(e.currentTarget.open)} style={{ padding: '8px 16px', fontSize: 12, maxHeight: '32vh', overflow: 'auto', flexShrink: 0 }}>
    <summary>AI usage for this conversation</summary>
    {open && (error ? <p role="alert">Couldn’t refresh AI usage.</p> : !report ? <p>Loading…</p> : <>
      <p>Estimated provider cost: ${report.estimatedCostUsd.toFixed(6)}. Updates every 5 seconds.</p>
      {report.unknownUsageCalls > 0 || report.unpricedCalls > 0 ? <p>Cost is incomplete: {report.unknownUsageCalls} calls without token counts; {report.unpricedCalls} calls without a price.</p> : null}
      <div style={{ overflowX: 'auto' }}><table aria-label="Conversation AI usage" style={{ minWidth: 640, width: '100%', textAlign: 'left', borderSpacing: '8px 4px' }}><thead><tr><th>Operation / model</th><th>Calls / failed / cached</th><th>Input / output tokens</th><th>Billable units</th><th>Search/action charge</th><th>First text p95</th></tr></thead>
        <tbody>{report.groups.map((g, i) => <tr key={i}><td>{g.kind} / {g.provider} / {g.model}</td><td>{g.calls} / {g.failed} / {g.cached}</td><td>{['chat', 'embedding'].includes(g.kind) ? `${g.inputTokens} / ${g.outputTokens}` : '—'}</td><td>{g.billableReplies + g.billableOperations}</td><td>{formatMoney(g.operationAmount, g.currency)}</td><td>{g.firstTokenP95Ms === null ? '—' : `${Math.round(g.firstTokenP95Ms)} ms`}</td></tr>)}</tbody>
      </table></div>
      {!report.groups.length ? <p>No AI calls for this conversation.</p> : null}
    </>)}
  </details>;
}
